/*!
 * bem v0.10.21 
 * (c) 2023 Alex Tkachenko
 * Released under the MIT License.
 */
var NAMESPACE = '';
var ELEMENT_DELIMITER = '__';
var MODIFIER_DELIMITER = '--';
var VALUE_DELIMITER = '_';
var STATE_PREFIX = 'is-';

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _isPlaceholder(a) {
  return a != null && _typeof(a) === 'object' && a['@@functional/placeholder'] === true;
}

var _isPlaceholder_1 = _isPlaceholder;

/**
 * Optimized internal one-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */

function _curry1(fn) {
  return function f1(a) {
    if (arguments.length === 0 || _isPlaceholder_1(a)) {
      return f1;
    } else {
      return fn.apply(this, arguments);
    }
  };
}

var _curry1_1 = _curry1;

/**
 * Tests whether or not an object is an array.
 *
 * @private
 * @param {*} val The object to test.
 * @return {Boolean} `true` if `val` is an array, `false` otherwise.
 * @example
 *
 *      _isArray([]); //=> true
 *      _isArray(null); //=> false
 *      _isArray({}); //=> false
 */
var _isArray = Array.isArray || function _isArray(val) {
  return val != null && val.length >= 0 && Object.prototype.toString.call(val) === '[object Array]';
};

function _isString(x) {
  return Object.prototype.toString.call(x) === '[object String]';
}

var _isString_1 = _isString;

/**
 * Tests whether or not an object is similar to an array.
 *
 * @private
 * @category Type
 * @category List
 * @sig * -> Boolean
 * @param {*} x The object to test.
 * @return {Boolean} `true` if `x` has a numeric length property and extreme indices defined; `false` otherwise.
 * @example
 *
 *      _isArrayLike([]); //=> true
 *      _isArrayLike(true); //=> false
 *      _isArrayLike({}); //=> false
 *      _isArrayLike({length: 10}); //=> false
 *      _isArrayLike({0: 'zero', 9: 'nine', length: 10}); //=> true
 */

var _isArrayLike =
/*#__PURE__*/
_curry1_1(function isArrayLike(x) {
  if (_isArray(x)) {
    return true;
  }

  if (!x) {
    return false;
  }

  if (_typeof(x) !== 'object') {
    return false;
  }

  if (_isString_1(x)) {
    return false;
  }

  if (x.nodeType === 1) {
    return !!x.length;
  }

  if (x.length === 0) {
    return true;
  }

  if (x.length > 0) {
    return x.hasOwnProperty(0) && x.hasOwnProperty(x.length - 1);
  }

  return false;
});

var _isArrayLike_1 = _isArrayLike;

/**
 * `_makeFlat` is a helper function that returns a one-level or fully recursive
 * function based on the flag passed in.
 *
 * @private
 */

function _makeFlat(recursive) {
  return function flatt(list) {
    var value, jlen, j;
    var result = [];
    var idx = 0;
    var ilen = list.length;

    while (idx < ilen) {
      if (_isArrayLike_1(list[idx])) {
        value = recursive ? flatt(list[idx]) : list[idx];
        j = 0;
        jlen = value.length;

        while (j < jlen) {
          result[result.length] = value[j];
          j += 1;
        }
      } else {
        result[result.length] = list[idx];
      }

      idx += 1;
    }

    return result;
  };
}

var _makeFlat_1 = _makeFlat;

/**
 * Returns a new list by pulling every item out of it (and all its sub-arrays)
 * and putting them in a new array, depth-first.
 *
 * @func
 * @memberOf R
 * @since v0.1.0
 * @category List
 * @sig [a] -> [b]
 * @param {Array} list The array to consider.
 * @return {Array} The flattened list.
 * @see R.unnest
 * @example
 *
 *      R.flatten([1, 2, [3, 4], 5, [6, [7, 8, [9, [10, 11], 12]]]]);
 *      //=> [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
 */

var flatten =
/*#__PURE__*/
_curry1_1(
/*#__PURE__*/
_makeFlat_1(true));

var flatten_1 = flatten;

var isArray = Array.isArray;
var isString = function isString(value) {
  return toString.call(value) === '[object String]';
};
var toArray = function toArray(value) {
  return isArray(value) ? value : [value];
};
function preparePairs(pairs) {
  return Object.keys(pairs || {}).reduce(function (acc, key) {
    flatten_1(toArray(pairs[key]), Infinity).filter(function (value) {
      return value;
    }).forEach(function (value) {
      return acc.push(value === true ? [key] : [key, value]);
    });
    return acc;
  }, []);
}

function generateModifierClassNames(className) {
  var modifiers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var m = arguments.length > 2 ? arguments[2] : undefined;
  var v = arguments.length > 3 ? arguments[3] : undefined;
  var prepared = preparePairs(modifiers).map(function (modifier) {
    return modifier.join(v);
  });
  var prefix = "".concat(className).concat(m);
  return prepared.map(function (modifier) {
    return prefix + modifier;
  });
}
function generateElementClassName(block) {
  var element = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var n = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var e = arguments.length > 3 ? arguments[3] : undefined;
  var stack = [(!n ? '' : n) + block];

  if (element) {
    stack.push(element);
  }

  return stack.join(e);
}
function generateClassNames(block) {
  var element = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var modifiers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var _ref = arguments.length > 3 ? arguments[3] : undefined,
      n = _ref.n,
      e = _ref.e,
      m = _ref.m,
      v = _ref.v;

  var className = generateElementClassName(block, element, n, e);
  var modifierClassNames = generateModifierClassNames(className, modifiers, m, v);
  return [className].concat(modifierClassNames);
}
var BlockGenerator =
/*#__PURE__*/
function () {
  function BlockGenerator() {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref2$n = _ref2.n,
        n = _ref2$n === void 0 ? NAMESPACE : _ref2$n,
        _ref2$e = _ref2.e,
        e = _ref2$e === void 0 ? ELEMENT_DELIMITER : _ref2$e,
        _ref2$m = _ref2.m,
        m = _ref2$m === void 0 ? MODIFIER_DELIMITER : _ref2$m,
        _ref2$v = _ref2.v,
        v = _ref2$v === void 0 ? VALUE_DELIMITER : _ref2$v;

    _classCallCheck(this, BlockGenerator);

    this.config = {
      n: n,
      e: e,
      m: m,
      v: v
    };
  }

  _createClass(BlockGenerator, [{
    key: "block",
    value: function block(_block, element) {
      var modifiers = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      return generateClassNames(_block, element, modifiers, this.config);
    }
  }]);

  return BlockGenerator;
}();
function createBlockNameGenerator(config) {
  var generator = new BlockGenerator(config);
  return function (block, eom, modifiers) {
    return generator.block(block, isString(eom) ? eom : undefined, modifiers || !isString(eom) && eom || {});
  };
}
function createBlockGenerator(config) {
  var generator = createBlockNameGenerator(config);
  return function (block, element) {
    return function (eom, modifiers) {
      return generator(block, isString(eom) ? eom : element, modifiers || !isString(eom) && eom || {});
    };
  };
}

var StateGenerator =
/*#__PURE__*/
function () {
  function StateGenerator() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref$sp = _ref.sp,
        sp = _ref$sp === void 0 ? STATE_PREFIX : _ref$sp,
        _ref$v = _ref.v,
        v = _ref$v === void 0 ? VALUE_DELIMITER : _ref$v;

    _classCallCheck(this, StateGenerator);

    this.config = {
      sp: sp,
      v: v
    };
  }

  _createClass(StateGenerator, [{
    key: "states",
    value: function states() {
      var _states = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

      return generateModifierClassNames(this.config.sp, _states, '', this.config.v);
    }
  }]);

  return StateGenerator;
}();
function createStateGenerator(config) {
  var generator = new StateGenerator(config);
  return generator.states.bind(generator);
}

export { BlockGenerator, ELEMENT_DELIMITER, MODIFIER_DELIMITER, NAMESPACE, STATE_PREFIX, StateGenerator, VALUE_DELIMITER, createBlockGenerator, createBlockNameGenerator, createStateGenerator, generateClassNames, generateElementClassName, generateModifierClassNames, isArray, isString, preparePairs, toArray };
